import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router'
import { Archive, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { commandCenterApi } from '../services/api'
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  DataView,
  Field,
  IconButton,
  PageHeader,
  PageLayout,
  PageToolbar,
  Pagination,
  SearchField,
  Select,
  StatusBadge,
} from './common'
import { membersModerationBadge } from './moderation/postGroupModerationModel'
import styles from './CommandCenterPostList.module.css'

const PAGE_SIZE = 20
const LIFECYCLE_FILTERS = [
  { value: '', label: 'All lifecycle states' },
  { value: 'active', label: 'Active' },
  { value: 'archived_manual', label: 'Archived' },
  { value: 'archived_settled', label: 'Settled and archived' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
]

function groupLabel(group) {
  return group.document?.shared?.label || 'Untitled distributed post'
}

function groupOrganizations(group) {
  return (group.destinations || [])
    .map((destination) => destination.organization_name || destination.organization_slug)
    .filter(Boolean)
}

function availableAction(group) {
  if (group.lifecycle === 'archived_settled') return null
  if (group.lifecycle === 'pending_delete') return 'undo-delete'
  if (group.lifecycle === 'archived_manual') return 'restore'
  return group.delete_capability?.allowed ? 'delete' : 'archive'
}

function mutationError(error) {
  return error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || 'The distributed post was not changed.'
}

function CommandCenterPostList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [lifecycleFilter, setLifecycleFilter] = useState('')
  const [search, setSearch] = useState('')
  const [actionTarget, setActionTarget] = useState(null)

  const groupsQuery = useQuery({
    queryKey: ['cc-post-groups', page, lifecycleFilter, search],
    queryFn: () => commandCenterApi.listPostGroups({
      page,
      per_page: PAGE_SIZE,
      lifecycle: lifecycleFilter || undefined,
      q: search.trim() || undefined,
    }),
  })

  const actionMutation = useMutation({
    mutationFn: ({ action, group }) => {
      if (action === 'delete') {
        return commandCenterApi.deletePostGroup(group.group_id, group.revision)
      }
      if (action === 'undo-delete') {
        return commandCenterApi.undoPendingDeletePostGroup(group.group_id, group.revision)
      }
      if (action === 'archive') {
        return commandCenterApi.archivePostGroup(group.group_id, group.revision, 'Archived by operator')
      }
      return commandCenterApi.restorePostGroup(group.group_id, group.revision)
    },
    onSuccess: (_data, variables) => {
      setActionTarget(null)
      toast.success(variables.action === 'delete'
        ? 'Distributed post marked for deletion'
        : variables.action === 'undo-delete'
          ? 'Distributed post deletion undone'
          : variables.action === 'archive'
            ? 'Distributed post archived'
            : 'Distributed post restored')
      queryClient.invalidateQueries({ queryKey: ['cc-post-groups'] })
    },
  })

  const groups = groupsQuery.data?.post_groups || []
  const total = groupsQuery.data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const columns = [
    {
      key: 'label',
      label: 'Distributed post',
      render: (group) => (
        <Link to={`/command-center/post-groups/${group.group_id}`} className={styles.groupLink}>
          {groupLabel(group)}
        </Link>
      ),
    },
    {
      key: 'organizations',
      label: 'Organizations',
      render: (group) => {
        const names = groupOrganizations(group)
        return names.length ? names.join(', ') : 'None'
      },
    },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      render: (group) => <StatusBadge auto>{group.lifecycle_label}</StatusBadge>,
    },
    {
      key: 'approval_status',
      label: 'Approval',
      render: (group) => <StatusBadge auto>{group.approval_status}</StatusBadge>,
    },
    {
      key: 'moderation',
      label: 'Moderation',
      // One chip for the books moderation holds, worst first.
      render: (group) => {
        const badge = membersModerationBadge(group.destinations || [])
        return badge ? <Badge compact tone={badge.tone}>{badge.label}</Badge> : '—'
      },
    },
    {
      key: 'created_by',
      label: 'Created by',
      render: (group) => group.created_by_admin?.display_name || group.created_by_admin?.email || '—',
    },
    {
      key: 'posts',
      label: 'Posts',
      numeric: true,
      render: (group) => (group.destinations || []).length,
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (group) => group.created_at ? new Date(group.created_at).toLocaleDateString() : '—',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (group) => {
        const action = availableAction(group)
        const actionLabel = action === 'delete' ? 'Delete' : action === 'undo-delete' ? 'Undo deletion' : action === 'archive' ? 'Archive' : 'Restore'
        return (
          <div className={styles.actions}>
            <Link to={`/command-center/post-groups/${group.group_id}`}>View</Link>
            <Link to={`/command-center/post-groups/${group.group_id}/edit`}>Edit</Link>
            {action ? (
              <IconButton
                label={`${actionLabel} ${groupLabel(group)}`}
                variant="ghost"
                onClick={() => setActionTarget({ action, group })}
              >
                {action === 'delete'
                  ? <Trash2 size={16} aria-hidden="true" />
                  : <Archive size={16} aria-hidden="true" />}
              </IconButton>
            ) : null}
          </div>
        )
      },
    },
  ]

  const filtered = Boolean(search.trim() || lifecycleFilter)
  const actionLabel = actionTarget?.action === 'delete'
    ? 'Delete'
    : actionTarget?.action === 'undo-delete'
      ? 'Undo deletion'
      : actionTarget?.action === 'archive'
        ? 'Archive'
        : 'Restore'

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Distributed posts"
        status={<StatusBadge status="default">{total} total</StatusBadge>}
        primaryAction={(
          <Button onClick={() => navigate('/command-center/posts/new')}>
            <Plus size={16} aria-hidden="true" />
            Create post
          </Button>
        )}
      />

      {actionMutation.error ? <Alert variant="error">{mutationError(actionMutation.error)}</Alert> : null}

      <DataView
        items={groups}
        loading={groupsQuery.isLoading}
        error={groupsQuery.error}
        filtered={filtered}
        emptyTitle="No distributed posts yet"
        emptyDescription="Create one distributed post to publish coordinated content across organizations."
        searchEmptyTitle="No distributed posts match"
        searchEmptyDescription="Change the search or lifecycle filter."
        onRetry={() => groupsQuery.refetch()}
        onClear={() => {
          setSearch('')
          setLifecycleFilter('')
          setPage(1)
        }}
        toolbar={(
          <PageToolbar
            label="Distributed post controls"
            search={(
              <SearchField
                label="Search distributed posts"
                placeholder="Search label, creator, or organization…"
                value={search}
                onChange={(value) => {
                  setSearch(value)
                  setPage(1)
                }}
                loading={groupsQuery.isFetching}
              />
            )}
            filters={(
              <Field label="Lifecycle">
                <Select
                  value={lifecycleFilter}
                  onChange={(event) => {
                    setLifecycleFilter(event.target.value)
                    setPage(1)
                  }}
                >
                  {LIFECYCLE_FILTERS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </Field>
            )}
          />
        )}
      >
        <DataTable data={groups} columns={columns} density="compact" label="Command Center distributed posts" />
        {totalPages > 1 ? <Pagination page={page} pageCount={totalPages} onPageChange={setPage} /> : null}
      </DataView>

      <ConfirmDialog
        open={Boolean(actionTarget)}
        title={`${actionLabel} distributed post`}
        message={actionTarget
          ? actionTarget.action === 'delete'
            ? `Mark “${groupLabel(actionTarget.group)}” and all ${(actionTarget.group.destinations || []).length} member posts for deletion? They will be erased after the retention window.`
            : actionTarget.action === 'undo-delete'
              ? `Undo deletion for “${groupLabel(actionTarget.group)}” and restore all ${(actionTarget.group.destinations || []).length} member posts?`
              : actionTarget.action === 'archive'
                ? `Archive “${groupLabel(actionTarget.group)}” and all ${(actionTarget.group.destinations || []).length} member posts?`
                : `Restore “${groupLabel(actionTarget.group)}” and all ${(actionTarget.group.destinations || []).length} member posts to their pre-archive state?`
          : ''}
        confirmLabel={actionLabel}
        onConfirm={() => actionTarget && actionMutation.mutate(actionTarget)}
        onCancel={() => setActionTarget(null)}
        loading={actionMutation.isPending}
        variant={actionTarget?.action === 'delete' ? 'danger' : 'default'}
      />
    </PageLayout>
  )
}

export default CommandCenterPostList
